import { FC, useState, useEffect, useMemo } from 'react';
import find from 'lodash/find';
import noop from 'utils/noop';
import debounce from 'lodash/debounce';

import { GROUP_BUTTON_SIZES } from 'ui/buttonGroup';
import {
  BasicOption,
  BasicOptionProps,
  CategoryHeader,
  CategoryLabel,
  ALIGNMENT,
  BaseOption,
  OnSelectHandler,
  SelectValueType,
} from 'ui/selects';
import { DelayUsingProperty, ESTypeMap } from 'modules/workflows/types';
import { getESDataTypeDisplayName } from 'modules/workflows';
import {
  parseEsMappings,
  getDefaultDataType,
  getDelayUsingPropertyValue,
  getDelayUsingPropertyOptions,
} from '../DelayEditor.utils';
import {
  PropertyDetails,
  StyledButtonGroup,
  StyledGroupButton,
  StyledBasicFilterSelect,
  StyledLeftTooltip,
} from '../DelayEditor.styles';
import { DELAY_USING_PROPERTY_OPTION_VALUE_SEPARATOR } from '../DelayEditor.constants';

interface FieldTypeOptionMap {
  [dataType: DataType]: BaseOption[];
}

/**
 * TODO: [PRO-5417] BasicSelect component should support categories
 * TODO: [WF-2770] Migrate DelayUsingPropertyForm to use BasicSelect with categories
 */
const getOptionOrCategoryLabel =
  (categories: FieldTypeOptionMap) => (props: BasicOptionProps<SelectValueType>) => {
    // Returns a category if current option is not a regular option
    if (props?.value && categories[props.value as string])
      return (
        <CategoryHeader>
          <CategoryLabel>{props.children}</CategoryLabel>
        </CategoryHeader>
      );

    // Return an option if not a category
    return <BasicOption {...props} />;
  };

type DelayIsAdditive = DelayUsingProperty['delayIsAdditive'];
type DataType = DelayUsingProperty['dataType'];
type Field = DelayUsingProperty['field'];

export type DelayUsingPropertyFormProps = DelayUsingProperty & {
  /** Is the delay additive?  IE: does it come after the specified date */
  delayIsAdditive?: DelayIsAdditive;
  /** The datatype to delay by */
  dataType?: DataType;
  /** The field to delay by */
  field?: Field;
  /** Mapping for es types */
  mappings?: ESTypeMap;
  /** Callback for when `delayIsAdditive` is updated */
  onIsAdditiveChange?: (e: DelayIsAdditive) => void;
  /** Callback for when `dataType` is updated */
  onDataTypeChange?: (e: DataType) => void;
  /** Callback for when `field` is updated */
  onFieldChange?: (e: Field) => void;
  /** Cloning across project error validation */
  isError?: boolean;
  /** Is the form readonly? */
  isReadOnly?: boolean;
  /** Display value should there be errors in Cloning across projects */
  displayValue?: string;
};

export const DelayUsingPropertyForm: FC<React.PropsWithChildren<DelayUsingPropertyFormProps>> = ({
  delayIsAdditive,
  dataType,
  field,
  mappings = {},
  onIsAdditiveChange = noop,
  onDataTypeChange = noop,
  onFieldChange = noop,
  displayValue,
  isError,
  isReadOnly = false,
}: DelayUsingPropertyFormProps): JSX.Element => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [fieldsByDataType, setFieldsByDataType] = useState<FieldTypeOptionMap>({});
  const [dataTypeOptions, setDataTypeOptions] = useState<BaseOption[]>([]);
  const [filter, setFilter] = useState('');

  const handleDataTypeChange = (newDataType: DataType) => {
    const dataTypeFields = fieldsByDataType[newDataType] || [];

    // clear the field value if it doesn't correspond to a property on the datatype
    if (field && !find(dataTypeFields, { value: field })) {
      onFieldChange('');
    }

    if (newDataType !== dataType) {
      onDataTypeChange(newDataType);
    }
  };

  useEffect(() => {
    if (typeof delayIsAdditive !== 'boolean') {
      onIsAdditiveChange(false);
    }
  }, []);

  useEffect(() => {
    if (mappings) {
      const { dataTypes, fieldMap } = parseEsMappings(mappings);

      setDataTypeOptions(dataTypes);
      setFieldsByDataType(fieldMap);

      if (!dataType) {
        const currentDataType = getDefaultDataType(dataTypes);
        onDataTypeChange(currentDataType);
        handleDataTypeChange(currentDataType);
      }

      setIsInitialized(true);
    }
  }, [mappings]);

  const defaultValues = field && dataType ? [getDelayUsingPropertyValue(dataType, field)] : [];

  const options = useMemo(
    () => getDelayUsingPropertyOptions(dataTypeOptions, fieldsByDataType, filter),
    [dataTypeOptions, fieldsByDataType, filter],
  );

  const handleSearchChange = (filterText = '') => setFilter(filterText);
  const onSelectProperty: OnSelectHandler<SelectValueType> = ([value]) => {
    /**
     * Extract field and dataType... see `getDelayUsingPropertyOptions`
     *
     * As of WF-3592, we are splitting the original value up in this fashion
     * to account for custom event names with instances of dashes within them
     * (which happens to also be our delimiter between the data type and field).
     * This ensure the split function preserves the entire event name.
     *
     * Example: the value 'customEvent-really-cool-event' should be parsed to
     *
     * newDataType: customEvent
     * newField: really-cool-event
     */
    const [category, ...rest] = (value as string)?.split?.(
      DELAY_USING_PROPERTY_OPTION_VALUE_SEPARATOR,
    );
    const newDataType = value ? category : dataType;
    const newField = value ? rest.join(DELAY_USING_PROPERTY_OPTION_VALUE_SEPARATOR) : field;

    // Updated field and dataType
    onDataTypeChange(newDataType);
    onFieldChange(newField);
  };

  return (
    <PropertyDetails>
      {isInitialized && (
        <>
          <StyledLeftTooltip
            isEnabled={isReadOnly}
            tooltipContent={`${getESDataTypeDisplayName(dataType)}: ${field}`}
          >
            <StyledBasicFilterSelect
              id="select-property-data-type"
              horizontalAlignment={ALIGNMENT.LEFT}
              placeholder={displayValue || 'Select a property...'}
              options={options}
              defaultValues={defaultValues}
              disableInternalFiltering
              onSelect={onSelectProperty}
              Option={getOptionOrCategoryLabel(fieldsByDataType)}
              onSearchChange={debounce(handleSearchChange, 300)}
              isError={isError}
              isReadOnly={isReadOnly}
              isDisabled={isReadOnly}
              isFullWidth
            />
          </StyledLeftTooltip>
          <StyledButtonGroup
            buttonSize={GROUP_BUTTON_SIZES.SMALL}
            defaultSelectedId={`is-additive-${delayIsAdditive}`}
            id="is-additive-button-group"
            isDisabled={isReadOnly}
          >
            <StyledGroupButton id="is-additive-false" onSelect={() => onIsAdditiveChange(false)}>
              Minus
            </StyledGroupButton>
            <StyledGroupButton id="is-additive-true" onSelect={() => onIsAdditiveChange(true)}>
              Plus
            </StyledGroupButton>
          </StyledButtonGroup>
        </>
      )}
    </PropertyDetails>
  );
};
